/**
 * AdminHostDashboardsPage (Iter 9.39c)
 *
 * The master list of every host dashboard with three views:
 *   - Kanban (columns by stage: draft / pending / activated / deactivated)
 *   - Table  (sortable columns)
 *   - Grid   (thumbnail cards with cover + host + stage badge)
 *
 * Each row has:
 *   - Preview (opens /h/{event_id} in a new tab - exactly what the host sees)
 *   - Link-to-Org dropdown (dropdown + Save sets organization_id on the event)
 */
import { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  LayoutDashboard, ExternalLink, Loader2, Search, LayoutGrid, Table as TableIcon, Columns,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const STAGES = [
  { value: "draft",       label: "Draft / Pending", hex: "#fef3c7" },
  { value: "activated",   label: "Active",          hex: "#d1fae5" },
  { value: "deactivated", label: "Deactivated",     hex: "#fee2e2" },
];

// Treat legacy `pending` stage as draft; 'active' alias maps to 'activated' (Iter 9.39d - merged).
const stageOf = (d) => {
  const s = d.dashboard_status || (d.is_pilot ? "activated" : "draft");
  if (s === "pending") return "draft";
  if (s === "active") return "activated";
  return s;
};

export default function AdminHostDashboardsPage() {
  const [dashboards, setDashboards] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban"); // kanban | table | grid
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dh, og] = await Promise.all([
        fetch(`${API}/admin/all-host-dashboards`, { headers: auth() }).then(r => r.ok ? r.json() : { dashboards: [] }),
        fetch(`${API}/admin/organizations`, { headers: auth() }).then(r => r.ok ? r.json() : { organizations: [] }),
      ]);
      setDashboards(dh.dashboards || []);
      setOrgs(og.organizations || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = dashboards.filter((d) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return String(d.name || "").toLowerCase().includes(q)
      || String(d.category || "").toLowerCase().includes(q)
      || String(d.host_user_id || "").toLowerCase().includes(q);
  });

  const linkToOrg = async (eventId, orgId) => {
    if (!orgId) return;
    const r = await fetch(`${API}/admin/organizations/${orgId}/link-event/${eventId}`, {
      method: "POST", headers: auth(),
    });
    if (r.ok) {
      toast.success("Linked to org");
      // Iter 9.39d - also auto-promote to activated if still a draft
      const dash = dashboards.find((d) => d.event_id === eventId);
      if (dash && (dash.dashboard_status === "draft" || dash.dashboard_status === "pending" || !dash.dashboard_status)) {
        await setStage(eventId, "activated");
      } else {
        load();
      }
    } else toast.error("Link failed");
  };

  const setStage = async (eventId, newStage) => {
    const r = await fetch(`${API}/admin/host-dashboards/${eventId}/stage`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ stage: newStage }),
    });
    if (r.ok) { toast.success(`Set to ${newStage}`); load(); }
    else toast.error("Stage update failed");
  };

  const openPreview = (eventId) => {
    window.open(`/host/${eventId}`, "_blank", "noreferrer");
  };
  const openEdit = (eventId) => {
    window.open(`/host/${eventId}?admin_edit=1`, "_blank", "noreferrer");
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-host-dashboards-page">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <LayoutDashboard className="w-5 h-5" />
        <h1 className="text-xl font-bold">Host Dashboards</h1>
        <Badge className="bg-purple-100 text-purple-700 border-0">{dashboards.length}</Badge>
        <div className="flex-1" />
        <div className="relative max-w-xs">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dashboards…" className="pl-8" data-testid="dashboards-search" />
        </div>
        <div className="flex gap-1 border rounded-lg p-0.5">
          <button className={`px-2 py-1 rounded ${view === "kanban" ? "bg-purple-100 text-purple-700" : "text-gray-400"}`} onClick={() => setView("kanban")} data-testid="dashboards-view-kanban"><Columns className="w-4 h-4" /></button>
          <button className={`px-2 py-1 rounded ${view === "table" ? "bg-purple-100 text-purple-700" : "text-gray-400"}`} onClick={() => setView("table")} data-testid="dashboards-view-table"><TableIcon className="w-4 h-4" /></button>
          <button className={`px-2 py-1 rounded ${view === "grid" ? "bg-purple-100 text-purple-700" : "text-gray-400"}`} onClick={() => setView("grid")} data-testid="dashboards-view-grid"><LayoutGrid className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : view === "kanban" ? (
        <KanbanView rows={filtered} orgs={orgs} onLink={linkToOrg} setStage={setStage} onPreview={openPreview} onEdit={openEdit} />
      ) : view === "table" ? (
        <TableView rows={filtered} orgs={orgs} onLink={linkToOrg} setStage={setStage} onPreview={openPreview} onEdit={openEdit} />
      ) : (
        <GridView rows={filtered} orgs={orgs} onLink={linkToOrg} setStage={setStage} onPreview={openPreview} onEdit={openEdit} />
      )}
    </div>
  );
}

// ─── Kanban ───
function KanbanView({ rows, orgs, onLink, setStage, onPreview, onEdit }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboards-kanban">
      {STAGES.map((s) => {
        const col = rows.filter((r) => stageOf(r) === s.value);
        return (
          <div key={s.value} className="rounded-xl" style={{ background: s.hex }}>
            <div className="px-3 py-2 border-b border-black/5 flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider">{s.label}</span>
              <Badge variant="outline" className="bg-white text-[10px]">{col.length}</Badge>
            </div>
            <div className="p-2 space-y-2 max-h-[540px] overflow-y-auto">
              {col.map((r) => <DashboardCard key={r.event_id} row={r} orgs={orgs} onLink={onLink} setStage={setStage} onPreview={onPreview} onEdit={onEdit} />)}
              {col.length === 0 && <div className="text-[11px] text-gray-400 p-2">Empty</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DashboardCard({ row, orgs, onLink, setStage, onPreview, onEdit }) {
  const eid = row.event_id;
  const [orgChoice, setOrgChoice] = useState(row.organization_id || "");
  const stage = stageOf(row);
  return (
    <Card className="border" data-testid={`dashboard-card-${eid}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          {/* Iter 9.47.f - host avatar uses Hosts.Logo if present, else
              Categories.Default Logo, else a neutral letter avatar (no more
              hardcoded pink gradient). */}
          {row.logo_url || row.cover_url || row.host_logo_url ? (
            <img
              src={row.logo_url || row.host_logo_url || row.cover_url}
              alt={row.name}
              className="w-8 h-8 rounded-lg shrink-0 object-cover"
              data-testid={`dashboard-avatar-${eid}`}
            />
          ) : (
            <div
              className="w-8 h-8 rounded-lg shrink-0 bg-gray-100 text-gray-500 flex items-center justify-center text-[11px] font-semibold uppercase"
              data-testid={`dashboard-avatar-fallback-${eid}`}
            >
              {(row.name || "?").trim().slice(0, 2)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{row.name || "(no name)"}</div>
            <div className="text-[11px] text-gray-500 truncate">
              {row.category ? <span className="capitalize">{row.category}</span> : "-"}
              {row.is_demo && <Badge variant="outline" className="ml-1 text-[9px]">DEMO</Badge>}
              {row.is_pilot && <Badge className="ml-1 bg-blue-100 text-blue-800 border-0 text-[9px]">Pilot</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <button type="button" onClick={() => onPreview(eid)} className="text-purple-600 hover:underline inline-flex items-center" data-testid={`dashboard-preview-${eid}`}>
            Preview <ExternalLink className="w-3 h-3 ml-0.5" />
          </button>
          <span className="text-gray-300">·</span>
          <button type="button" onClick={() => onEdit(eid)} className="text-gray-600 hover:underline" data-testid={`dashboard-edit-${eid}`}>
            Edit settings
          </button>
        </div>
        <div className="flex items-center gap-1">
          <Select value={orgChoice} onValueChange={setOrgChoice}>
            <SelectTrigger className="h-7 text-[11px]" data-testid={`dashboard-link-org-${eid}`}><SelectValue placeholder="Link to org…" /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id} className="text-xs">{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 px-2" disabled={!orgChoice || orgChoice === row.organization_id} onClick={() => onLink(eid, orgChoice)} data-testid={`dashboard-link-save-${eid}`}>
            Link
          </Button>
        </div>
        {/* Stage actions */}
        <div className="flex items-center gap-1 text-[11px]">
          {stage !== "activated" && (
            <Button size="sm" className="h-6 px-2 bg-green-600 hover:bg-green-700 text-white text-[11px]" onClick={() => setStage(eid, "activated")} data-testid={`dashboard-activate-${eid}`}>
              Activate
            </Button>
          )}
          {stage === "activated" && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setStage(eid, "deactivated")} data-testid={`dashboard-deactivate-${eid}`}>
              Deactivate
            </Button>
          )}
          {stage === "deactivated" && (
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setStage(eid, "draft")} data-testid={`dashboard-reopen-${eid}`}>
              Reopen as draft
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Table ───
function TableView({ rows, orgs, onLink, setStage, onPreview, onEdit }) {
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dashboards-table">
          <thead>
            <tr className="border-b bg-gray-50/60 text-left">
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-medium">Dashboard</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-medium">Category</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-medium">Stage</th>
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.event_id} className="border-b hover:bg-gray-50/40">
                <td className="px-3 py-2 font-medium">{r.name || "(no name)"}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] capitalize">{r.category}</Badge></td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] capitalize">{stageOf(r)}</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => onPreview(r.event_id)} className="text-xs text-purple-600 hover:underline inline-flex items-center" data-testid={`dashboards-preview-${r.event_id}`}>
                      Preview <ExternalLink className="w-3 h-3 ml-0.5" />
                    </button>
                    <button type="button" onClick={() => onEdit(r.event_id)} className="text-xs text-gray-600 hover:underline" data-testid={`dashboards-edit-${r.event_id}`}>
                      Edit
                    </button>
                    <LinkOrgInline eventId={r.event_id} orgs={orgs} onLink={onLink} />
                    {stageOf(r) !== "activated"
                      ? <Button size="sm" className="h-6 px-2 bg-green-600 hover:bg-green-700 text-white text-[11px]" onClick={() => setStage(r.event_id, "activated")} data-testid={`dashboards-activate-${r.event_id}`}>Activate</Button>
                      : <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setStage(r.event_id, "deactivated")} data-testid={`dashboards-deactivate-${r.event_id}`}>Deactivate</Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LinkOrgInline({ eventId, orgs, onLink }) {
  const [val, setVal] = useState("");
  return (
    <div className="inline-flex items-center gap-1">
      <Select value={val} onValueChange={setVal}>
        <SelectTrigger className="h-7 text-[11px] w-40" data-testid={`dashboards-link-select-${eventId}`}><SelectValue placeholder="Link to org…" /></SelectTrigger>
        <SelectContent>
          {orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id} className="text-xs">{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" className="h-7 px-2" disabled={!val} onClick={() => onLink(eventId, val)} data-testid={`dashboards-link-save-${eventId}`}>
        Link
      </Button>
    </div>
  );
}

// ─── Grid ───
function GridView({ rows, orgs, onLink, setStage, onPreview, onEdit }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dashboards-grid">
      {rows.map((r) => <DashboardCard key={r.event_id} row={r} orgs={orgs} onLink={onLink} setStage={setStage} onPreview={onPreview} onEdit={onEdit} />)}
    </div>
  );
}
